import type { RfqEventType, RfqHistoryEvent, RfqLine, RfqVendor } from "./types";

import type { RfqHistoryRow } from "./api";
import { toDateTimeLabel } from "./api";

export const RFQ_HISTORY_ACTION = {
  Created: "created",
  ProxyQuoteRecorded: "proxyQuoteRecorded",
  VendorDeclined: "vendorDeclined",
  ProxyNegotiationRecorded: "proxyNegotiationRecorded",
  SubmittedForReview: "submittedForReview",
  Approved: "approved",
  Rejected: "rejected",
} as const;

const ACTION_TO_EVENT: Record<string, RfqEventType> = {
  created: "created",
  proxyQuoteRecorded: "recorded",
  vendorDeclined: "decline",
  proxyNegotiationRecorded: "negotiate",
  submittedForReview: "submit",
  approved: "approve",
  rejected: "reject",
};

const ACTION_FALLBACK_TITLE: Record<string, string> = {
  created: "建立詢價單",
  proxyQuoteRecorded: "代錄報價",
  vendorDeclined: "廠商婉拒",
  proxyNegotiationRecorded: "代錄議價",
  submittedForReview: "送出審核",
  approved: "審核通過",
  rejected: "審核駁回",
};

export interface HistoryContext {
  lines: RfqLine[];
  vendors: RfqVendor[];
  employeeNames: Record<string, string>;
  t: (key: string, options?: Record<string, unknown>) => string;
  systemActor: string;
}

function readString(
  detail: Record<string, unknown> | null,
  key: string,
): string | undefined {
  const value = detail?.[key];
  return typeof value === "string" ? value : undefined;
}

function readNumber(
  detail: Record<string, unknown> | null,
  key: string,
): number | undefined {
  const value = detail?.[key];
  return typeof value === "number" ? value : undefined;
}

export function buildHistoryEvents(
  rows: RfqHistoryRow[],
  context: HistoryContext,
): RfqHistoryEvent[] {
  const { t, lines, vendors, employeeNames, systemActor } = context;
  const lineMap = new Map<string, RfqLine>();
  for (const line of lines) lineMap.set(line.id, line);
  const vendorMap = new Map<string, RfqVendor>();
  for (const vendor of vendors) vendorMap.set(vendor.id, vendor);

  const ordered = [...rows].sort((a, b) => {
    const left = new Date(a.occurredAt).getTime();
    const right = new Date(b.occurredAt).getTime();
    return left - right;
  });

  return ordered.map((row) => {
    const vendor = row.rfqQuoteId ? vendorMap.get(row.rfqQuoteId) : undefined;
    const vendorName = vendor?.name ?? "";
    const baseTitle =
      t(`rfq.history.action.${row.action}`) ||
      ACTION_FALLBACK_TITLE[row.action] ||
      row.action;
    const title = vendorName ? `${baseTitle}・${vendorName}` : baseTitle;

    const rfqItemId = readString(row.detail, "rfqItemId");
    const line = rfqItemId ? lineMap.get(rfqItemId) : undefined;
    const unitPrice = readNumber(row.detail, "unitPrice");
    const previousUnitPrice = readNumber(row.detail, "previousUnitPrice");
    const rejectedReason = readString(row.detail, "rejectedReason");

    let detail: string | undefined;
    if (row.action === RFQ_HISTORY_ACTION.Rejected && rejectedReason) {
      detail = t("rfq.history.detailRejected", {
        defaultValue: "駁回原因：{{reason}}",
        reason: rejectedReason,
      });
    } else if (
      row.action === RFQ_HISTORY_ACTION.ProxyNegotiationRecorded &&
      unitPrice != null
    ) {
      detail = t("rfq.history.detailNegotiation", {
        defaultValue: "{{item}} 單價 {{from}} → {{to}}",
        item: line?.name ?? "",
        from: previousUnitPrice ?? "—",
        to: unitPrice,
      });
    } else if (
      row.action === RFQ_HISTORY_ACTION.ProxyQuoteRecorded &&
      unitPrice != null
    ) {
      detail = t("rfq.history.detailQuote", {
        defaultValue: "{{item}} 單價 {{price}}",
        item: line?.name ?? "",
        price: unitPrice,
      });
    }

    return {
      id: row.id,
      type: ACTION_TO_EVENT[row.action] ?? "note",
      title,
      detail,
      actor: row.operatorId
        ? (employeeNames[row.operatorId] ?? systemActor)
        : systemActor,
      at: toDateTimeLabel(row.occurredAt),
    };
  });
}
